import React, { FC, useCallback, useMemo } from 'react';
import { AccountStatus, BaseAccount } from '../../../types';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  accountSlice,
  getEditableMode,
  getGroupBy,
  getSortBy,
  GroupAccountMembersBy,
  SortAccountMembersBy,
} from '../../../redux/account';
import { Stack, Tooltip, Typography, Alert, Box } from '@mui/material';
import CustomDatePicker from '../../../components/datePicker/CustomDatePicker';
import {
  groupAccountMembersOptions,
  sortOptionsForClientPartner,
} from '../../../features/budgets/MarginDetails.const';
import TabSwitcher from '../../../components/tabSwitcher/TabSwitcher';
import { paths } from '../../../constants/paths';
import { useLocation, useParams } from 'react-router-dom';
import WorkOffOutlinedIcon from '@mui/icons-material/WorkOffOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  DropdownMenu,
  DropdownMenuOption,
} from '../../../components/dropdown/DropdownMenu';

import { IconName } from '../../../components/customIcon/CustomIcon';
import { DateTime } from 'luxon';
import { isLastWeekOfCurrentQuarter } from '../../../utils';

interface Props {
  account: BaseAccount;
  dateFilterValueLocal: string | null;
}

export const AccountDetailsHeader: FC<Props> = ({
  account,
  dateFilterValueLocal,
}) => {
  const dispatch = useAppDispatch();
  const groupBy = useAppSelector(getGroupBy);
  const sortBy = useAppSelector(getSortBy);
  const isEditableMode = useAppSelector(getEditableMode);
  const { accountId, projectId } = useParams();

  const baseUrl = `${paths.portfolio}/${accountId}/${
    projectId ? projectId + '/' : ''
  }`;
  const location = useLocation();

  const onGroupByClick = useCallback(
    ({ value }: DropdownMenuOption<GroupAccountMembersBy>) => {
      dispatch(accountSlice.actions.setGroupBy(value));
    },
    [dispatch],
  );

  const onSortByClick = useCallback(
    ({ value }: DropdownMenuOption<SortAccountMembersBy>) => {
      dispatch(accountSlice.actions.setSortBy({ sortBy: value }));
    },
    [dispatch],
  );

  const shouldShowRevenueNotification = useMemo(() => {
    const currentQuarter = DateTime.now().toFormat('yyyyqq');
    const isLastWeek = isLastWeekOfCurrentQuarter();
    const latestStatus = account.latestQuarterlyRevenueStatus;

    return (
      isLastWeek &&
      (!latestStatus || latestStatus.quarter.toString() < currentQuarter)
    );
  }, [account]);

  return (
    <>
      <Stack
        flexDirection="row"
        alignItems="center"
        mb={2}
        minHeight="40px"
        sx={{ flexWrap: 'wrap', gap: 2 }}
      >
        {account.status === AccountStatus.Closed && (
          <Tooltip
            title="Closed"
            color="disabled"
            placement="top"
            sx={{ marginRight: '5px' }}
          >
            <WorkOffOutlinedIcon />
          </Tooltip>
        )}
        <Typography
          variant="h2"
          mx="2"
          marginRight="16px"
          maxWidth="300px"
          color={account.status === AccountStatus.Closed ? 'gray' : 'black'}
        >
          {account?.name}
        </Typography>
        <TabSwitcher
          tabs={[
            {
              linkTo: baseUrl + paths.projectStatus,
              linkName: projectId ? 'Project status' : 'Project statuses',
            },
            {
              linkTo: baseUrl + paths.ratesAndMargins,
              linkName: 'Rates and margins',
            },
            {
              linkTo: baseUrl + paths.sowManagement,
              linkName: 'SOW',
            },
            {
              linkTo: baseUrl + paths.timeAndPeople,
              linkName: 'Time and people',
            },
            {
              linkTo: baseUrl + paths.risks,
              linkName: 'Risks and alerts',
              Icon: account.alert ? (
                <NotificationsActiveIcon
                  sx={{ fontSize: '16px', color: 'error.main' }}
                />
              ) : null,
            },
            ...(projectId
              ? []
              : [
                  {
                    linkTo: baseUrl + paths.invoices,
                    linkName: 'Invoices',
                  },
                  {
                    linkTo: baseUrl + paths.forecast,
                    linkName: 'Forecast',
                    Icon: shouldShowRevenueNotification ? (
                      <NotificationsActiveIcon
                        sx={{ fontSize: '16px', color: 'error.main' }}
                      />
                    ) : null,
                  },
                ]),
          ]}
        />
        {location.pathname.includes(paths.ratesAndMargins) ? (
          <Stack flexDirection="row" gap={3} ml="auto">
            {!isEditableMode && (
              <DropdownMenu
                options={groupAccountMembersOptions}
                selectedItem={{ label: groupBy, value: groupBy }}
                onClick={onGroupByClick}
                startIcon={IconName.ViewDay}
              />
            )}
            <DropdownMenu
              options={sortOptionsForClientPartner}
              selectedItem={{ label: sortBy, value: sortBy }}
              onClick={onSortByClick}
              startIcon={IconName.Sort}
            />
            {/* Date picker button */}
            <CustomDatePicker
              setDateFilter={{
                type: 'dispatch',
                func: accountSlice.actions.setDateFilterValueLocal,
                setGlobalFunc: accountSlice.actions.setDateFilterValue,
                clean: accountSlice.actions.cleanDateFilterValues,
              }}
              dateFilterValueLocal={dateFilterValueLocal}
            />
          </Stack>
        ) : null}
        {location.pathname.includes(paths.sowManagement) ? (
          <Box
            id="sow-header-controls-portal-target"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              ml: 'auto',
              alignItems: 'center',
              gap: 1,
            }}
          />
        ) : null}
      </Stack>
      {account.status === AccountStatus.Closed && (
        <Alert
          severity="info"
          variant="standard"
          elevation={0}
          icon={<WorkOffOutlinedIcon color="secondary" />}
          sx={{
            marginBottom: 3,
          }}
        >
          <Typography variant="body1">Account is closed.</Typography>
        </Alert>
      )}
    </>
  );
};
